import { isFullCommitHash, isValidRefName } from "./git-ref.js";
import { parseRepoPath } from "./repo-path.js";
import { err, ok } from "./result.js";
import { hasForbiddenCodePoint } from "./text-safety.js";
// Grammar and semantics: docs/specs/address.md.
export const MAX_ADDRESS_LENGTH = 2048;
/** Git hosts an address can name. `gh` is GitHub. */
export const GIT_HOST_KEYS = ["gh"];
/**
 * The host of a pack served from a machine by the `skillcdn` command, with no git host behind it
 * (ADR-0053): `/local/<owner>/<name>` names a directory, as the command was told to call it. A
 * deployment serves no such address.
 */
export const LOCAL_HOST_KEY = "local";
/** Everything the first segment of an address can be: the git hosts, and `local`. */
export const ADDRESS_HOST_KEYS = [...GIT_HOST_KEYS, LOCAL_HOST_KEY];
export function isGitHostKey(value) {
    return GIT_HOST_KEYS.includes(value);
}
/** Whether an address names a repository on a git host, rather than a pack served from a machine. */
export function isGitHostAddress(address) {
    return isGitHostKey(address.host);
}
const HOST_NAMING_RULES = {
    gh: {
        // Managed-user accounts carry an underscore suffix, so "_" is allowed after the first character.
        owner: /^[a-z0-9][a-z0-9_-]{0,38}$/i,
        repo: /^[a-z0-9._-]{1,100}$/i,
        caseInsensitive: true,
    },
    // What a directory is called, as far as a URL path and a file system agree: no leading dot,
    // since a hidden directory is never served, and the case as written, since file systems differ.
    local: {
        owner: /^[a-z0-9_][a-z0-9._-]{0,99}$/i,
        repo: /^[a-z0-9_][a-z0-9._-]{0,99}$/i,
        caseInsensitive: false,
    },
};
function fail(code, message) {
    return err({ code, message });
}
function isAddressHost(value) {
    return ADDRESS_HOST_KEYS.includes(value);
}
function decodeSegment(raw) {
    try {
        return decodeURIComponent(raw);
    }
    catch {
        return undefined;
    }
}
/**
 * Parses the path of an endpoint URL, for example `/gh/acme/skills@v1.2.0/marketing`.
 * Total: every input yields an address or a typed error, and nothing here touches the network.
 */
export function parseAddress(input) {
    if (!input.startsWith("/")) {
        return fail("not_absolute", 'an address starts with "/"');
    }
    if (input.length > MAX_ADDRESS_LENGTH) {
        return fail("too_long", `an address is at most ${MAX_ADDRESS_LENGTH} characters`);
    }
    const segments = [];
    for (const raw of input.slice(1).split("/")) {
        if (raw.length === 0) {
            return fail("empty_segment", "an address has no empty segments and no trailing slash");
        }
        const segment = decodeSegment(raw);
        if (segment === undefined) {
            return fail("bad_encoding", "an address must be valid percent-encoded UTF-8");
        }
        // An encoded slash would mean different things before and after a proxy decodes it.
        if (segment.includes("/") || segment.includes("\\") || hasForbiddenCodePoint(segment)) {
            return fail("forbidden_character", "an address has no encoded slashes, backslashes or control characters");
        }
        if (segment === "." || segment === "..") {
            return fail("dot_segment", 'an address has no "." or ".." segments');
        }
        segments.push(segment);
    }
    const [host, owner, repoSegment, ...rest] = segments;
    if (host === undefined || !isAddressHost(host)) {
        return fail("unknown_host", `unknown host; expected one of: ${ADDRESS_HOST_KEYS.join(", ")}`);
    }
    if (owner === undefined || repoSegment === undefined) {
        return fail("missing_repo", "an address names an owner and a repository");
    }
    const rules = HOST_NAMING_RULES[host];
    if (!rules.owner.test(owner)) {
        return fail("invalid_owner", "the owner is not a valid account name on this git host");
    }
    const at = repoSegment.indexOf("@");
    const repo = at < 0 ? repoSegment : repoSegment.slice(0, at);
    if (!rules.repo.test(repo) || repo === "." || repo === ".." || /\.git$/i.test(repo)) {
        return fail("invalid_repo", "the repository is not a valid repository name on this git host");
    }
    let ref;
    let pathInput;
    if (at < 0) {
        pathInput = rest.join("/");
    }
    else {
        // The ref ends at the first ":" if there is one, otherwise at the first "/".
        const tail = [repoSegment.slice(at + 1), ...rest].join("/");
        const colon = tail.indexOf(":");
        const end = colon >= 0 ? colon : tail.indexOf("/");
        const refText = end < 0 ? tail : tail.slice(0, end);
        pathInput = end < 0 ? "" : tail.slice(end + 1);
        if (refText.length === 0) {
            return fail("empty_ref", 'a ref follows "@"');
        }
        if (isFullCommitHash(refText)) {
            ref = { kind: "commit", hash: refText.toLowerCase() };
        }
        else if (isValidRefName(refText)) {
            ref = { kind: "name", name: refText };
        }
        else {
            return fail("invalid_ref", "the ref is not a valid git ref name");
        }
    }
    const path = parseRepoPath(pathInput);
    if (!path.ok) {
        return fail("invalid_path", path.error.message);
    }
    return ok({
        host,
        owner: rules.caseInsensitive ? owner.toLowerCase() : owner,
        repo: rules.caseInsensitive ? repo.toLowerCase() : repo,
        ref,
        path: path.value,
    });
}
function encodeSlashSeparated(value) {
    return value
        .split("/")
        .map((segment) => encodeURIComponent(segment).replaceAll("%40", "@").replaceAll("%3A", ":"))
        .join("/");
}
/** The canonical spelling of an address. `formatAddress(parseAddress(x).value)` is the canonical form of `x`. */
export function formatAddress(address) {
    const repo = `/${address.host}/${address.owner}/${address.repo}`;
    const path = encodeSlashSeparated(address.path);
    if (address.ref === undefined) {
        return path.length === 0 ? repo : `${repo}/${path}`;
    }
    const refText = address.ref.kind === "commit" ? address.ref.hash : address.ref.name;
    const mounted = `${repo}@${encodeSlashSeparated(refText)}`;
    // Without the explicit terminator, a "/" in the ref or a ":" in the path would be read back differently.
    if (refText.includes("/") || address.path.includes(":")) {
        return `${mounted}:${path}`;
    }
    return path.length === 0 ? mounted : `${mounted}/${path}`;
}
/**
 * Parses `/gh/<owner>`: the path of the page of an account. It is one segment short of an
 * address, serves people and never MCP. `undefined` for anything else; an owner is written with
 * unreserved characters only, so nothing is percent-decoded. Only a git host has accounts: a
 * `local` owner is a name the command was given, and has no page.
 */
export function parseOwnerPath(input) {
    if (input.length > MAX_ADDRESS_LENGTH) {
        return undefined;
    }
    const [empty, host, owner, ...rest] = input.split("/");
    if (empty !== "" || host === undefined || owner === undefined || rest.length > 0) {
        return undefined;
    }
    if (!isGitHostKey(host)) {
        return undefined;
    }
    const rules = HOST_NAMING_RULES[host];
    return rules.owner.test(owner)
        ? { host, owner: rules.caseInsensitive ? owner.toLowerCase() : owner }
        : undefined;
}
export function formatOwnerPath(owner) {
    return `/${owner.host}/${owner.owner}`;
}
/** Pinned addresses name a full commit hash and may be cached indefinitely; everything else moves. */
export function isPinnedAddress(address) {
    return address.ref?.kind === "commit";
}
//# sourceMappingURL=address.js.map